/**
 * Uni Forms — per-form-type activity summary.
 * Running saved templates lives on Report templates.
 */
import React from 'react';
import { Link, Navigate, useNavigate, useSearchParams } from 'react-router-dom';
import { ROUTES } from '../../config.js';
import {
  fmtCount,
  UniActionButton, UniFilterSelect, UniKpiCard,
  UniPageShell, UniSection, UniTable, UniToolbar,
  useUniList,
} from './UniShared.jsx';
import { formatInTimeZone, useOrgTimeZone } from '../../lib/displayTimezone.js';

const EP = {
  SUMMARY: '/api/uni-forms/report-summary/',
};

const THEME = 'forms';

const DATE_RANGE_OPTIONS = [
  { value: 'today', label: 'Today' },
  { value: 'yesterday', label: 'Yesterday' },
  { value: 'last_7', label: 'Last 7 days' },
  { value: 'last_30', label: 'Last 30 days' },
  { value: 'this_month', label: 'This month' },
  { value: 'last_month', label: 'Last month' },
  { value: 'custom', label: 'Custom dates' },
];

export function UniFormsReports() {
  const tz = useOrgTimeZone();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const viewFromUrl = searchParams.get('view');
  const [summaryRange, setSummaryRange] = React.useState('last_30');
  const summary = useUniList(`${EP.SUMMARY}?date_range=${summaryRange}`, [summaryRange]);

  if (viewFromUrl) {
    const rest = new URLSearchParams(searchParams);
    rest.delete('view');
    const suffix = rest.toString();
    const target = `${ROUTES.UNI_FORMS_REPORT_TEMPLATES}?view=${encodeURIComponent(viewFromUrl)}${suffix ? `&${suffix}` : ''}`;
    return <Navigate to={target} replace />;
  }

  const summaryColumns = [
    { key: 'label', label: 'Form type',
      render: (r) => <span style={{ fontWeight: 600 }}>{r.label}</span> },
    { key: 'form_count', label: 'Forms', align: 'right',
      render: (r) => <span className="uni-num">{fmtCount(r.form_count)}</span> },
    { key: 'internal_count', label: 'Internal', align: 'right',
      render: (r) => <span className="uni-num uni-num--muted">{fmtCount(r.internal_count)}</span> },
    { key: 'response_count', label: 'Submissions', align: 'right',
      render: (r) => <span className="uni-num">{fmtCount(r.response_count)}</span> },
    { key: 'people_count', label: 'People', align: 'right',
      render: (r) => <span className="uni-num">{fmtCount(r.people_count)}</span> },
    { key: 'last_submission', label: 'Last activity',
      render: (r) => (r.last_submission
        ? formatInTimeZone(r.last_submission, tz)
        : '—') },
    { key: 'actions', label: '', align: 'right',
      render: (r) => (
        <button type="button" className="uni-row-btn"
                onClick={() => navigate(
                  `${ROUTES.UNI_FORMS_REPORT_TEMPLATE_NEW}?form_type=${encodeURIComponent(r.form_type)}`,
                )}>
          New template
        </button>
      ) },
  ];

  return (
    <UniPageShell
      theme={THEME}
      icon="📈"
      title="Reports"
      subtitle="Activity per form type. Run saved templates from Report templates."
      actions={
        <>
          <UniActionButton hero to={ROUTES.UNI_FORMS_REPORT_TEMPLATES}>📋 Templates</UniActionButton>
          <UniActionButton to="/uni-forms">← Dashboard</UniActionButton>
        </>
      }
    >
      <UniSection title="Every form type at a glance" />
      <div className="uni-kpis">
        <UniKpiCard
          icon="📨" label="Submissions"
          value={summary.loading ? '…' : fmtCount(
            (summary.raw?.form_types || []).reduce((a, b) => a + (b.response_count || 0), 0),
          )}
          sublabel={summary.raw ? `${summary.raw.date_from} → ${summary.raw.date_to}` : ''}
          to="/uni-forms/forms"
        />
        <UniKpiCard
          icon="🗂️" label="Form types"
          value={summary.loading ? '…' : fmtCount((summary.raw?.form_types || []).length)}
          sublabel="in use"
        />
        <UniKpiCard
          icon="🔐" label="Internal forms"
          value={summary.loading ? '…' : fmtCount(
            (summary.raw?.form_types || []).reduce((a, b) => a + (b.internal_count || 0), 0),
          )}
          sublabel="not publicly fillable"
          to="/uni-forms/forms"
        />
        <UniKpiCard
          icon="💾" label="Saved reports"
          value={summary.loading ? '…' : fmtCount(summary.raw?.saved_report_count)}
          sublabel="available to you"
          to={ROUTES.UNI_FORMS_REPORT_TEMPLATES}
        />
      </div>

      <UniToolbar>
        <UniFilterSelect
          label="Period" value={summaryRange} onChange={setSummaryRange}
          options={DATE_RANGE_OPTIONS.filter((o) => o.value !== 'custom')}
        />
      </UniToolbar>
      <UniTable
        rows={summary.raw?.form_types || []}
        loading={summary.loading} error={summary.error}
        columns={summaryColumns} rowKey="form_type"
        empty="No submissions in this period"
        emptyHint="Publish a form and collect a response to see it here."
      />

      <UniSection title="Run a saved template" />
      <div className="uni-note uni-note--info">
        <span aria-hidden="true">ℹ️</span>
        <span>
          Open{' '}
          <Link to={ROUTES.UNI_FORMS_REPORT_TEMPLATES}>Report templates</Link>
          , pick a template and click <strong>Run</strong>. Results stay on that page.
        </span>
      </div>
    </UniPageShell>
  );
}
